import { BaseFormData } from './types';

// Standardized validation rules for consistency across all form steps
export const ValidationRules = {
  // Text field validations
  required: (fieldName: string) => ({
    required: `${fieldName} is required`
  }),

  minLength: (fieldName: string, min: number) => ({
    minLength: {
      value: min,
      message: `${fieldName} must be at least ${min} characters`
    }
  }),

  maxLength: (fieldName: string, max: number) => ({
    maxLength: {
      value: max,
      message: `${fieldName} must be no more than ${max} characters`
    }
  }),

  // Email validation
  email: (fieldName: string = 'Email') => ({
    required: `${fieldName} is required`,
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address'
    }
  }),

  // Phone validation
  phone: (fieldName: string = 'Phone number') => ({
    required: `${fieldName} is required`,
    pattern: {
      value: /^[\+]?[\d\s]{8,20}$/,
      message: 'Please enter a valid phone number'
    },
    validate: (value: string) => {
      // Remove spaces to count actual digits
      const digitsOnly = value?.replace(/\s/g, '') || '';
      if (digitsOnly.length < 8) {
        return 'Phone number must be at least 8 digits';
      }
      if (digitsOnly.length > 15) {
        return 'Phone number cannot exceed 15 digits';
      }
      // Check if it starts with + or digit
      if (!/^[\+]?[0-9]/.test(digitsOnly)) {
        return 'Phone number must start with + or a digit';
      }
      return true;
    }
  }),

  // ABN validation
  abn: (fieldName: string = 'ABN') => ({
    required: `${fieldName} is required`,
    validate: (value: string) => {
      const cleanedValue = value?.replace(/\s/g, '') || '';
      if (!/^\d{11}$/.test(cleanedValue)) {
        return 'ABN must be exactly 11 digits';
      }
      return true;
    }
  }),

  abnOrAcn: (fieldName: string = 'ABN/ACN') => ({
    required: `${fieldName} is required`,
    validate: (value: string) => {
      const cleanedValue = value?.replace(/\s/g, '') || '';
      if (!/^\d{11}$|^\d{9}$/.test(cleanedValue)) {
        return 'ABN/ACN must be exactly 11 digits (ABN) or 9 digits (ACN)';
      }
      return true;
    }
  }),

  // ABN/NZBN validation based on country
  abnOrNzbn: (fieldName: string = 'ABN/NZBN', country?: string) => ({
    required: `${fieldName} is required`,
    validate: (value: string) => {
      const cleanedValue = value?.replace(/\s/g, '') || '';
      
      if (country === 'New Zealand') {
        // NZBN validation: 13 digits
        if (!/^\d{13}$/.test(cleanedValue)) {
          return 'NZBN must be exactly 13 digits';
        }
      } else {
        // Default to ABN validation: 11 digits (Australia or unknown country)
        if (!/^\d{11}$/.test(cleanedValue)) {
          return 'ABN must be exactly 11 digits';
        }
      }
      return true;
    }
  }),

  // Postcode validation
  postcode: (fieldName: string = 'Postcode') => ({
    required: `${fieldName} is required`,
    pattern: {
      value: /^\d{4}$/,
      message: 'Postcode must be exactly 4 digits'
    }
  }),

  // Business name validation
  businessName: (fieldName: string = 'Business name') => ({
    required: `${fieldName} is required`,
    minLength: {
      value: 2,
      message: `${fieldName} must be at least 2 characters`
    }
  }),

  // Number validation
  positiveNumber: () => ({
    validate: (value: string | number) => {
      if (!value) return true; // Optional fields
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return 'Please enter a valid number';
      if (numValue <= 0) return 'Please enter a positive number';
      return true;
    }
  }),

  // Integer validation
  positiveInteger: () => ({
    validate: (value: string | number) => {
      if (!value) return true; // Optional fields
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return 'Please enter a valid number';
      if (numValue <= 0 || !Number.isInteger(numValue)) return 'Please enter a positive whole number';
      return true;
    }
  }),

  // Checkbox validation (for terms acceptance)
  mustBeTrue: (fieldName: string) => ({
    validate: (value: boolean) => value === true || `You must accept the ${fieldName}`
  }),

  // Selection validation
  selection: (fieldName: string) => ({
    required: `Please select a ${fieldName}`
  }),

  // Array validation (for multi-select)
  arrayMinLength: (fieldName: string, min: number = 1) => ({
    validate: (value: unknown[]) => {
      if (!Array.isArray(value) || value.length < min) {
        return `Please select at least ${min} ${fieldName}${min === 1 ? '' : 's'}`;
      }
      return true;
    }
  }),

  // URL validation
  url: () => ({
    pattern: {
      value: /^https?:\/\/.+$/,
      message: 'Please enter a valid URL starting with http:// or https://'
    }
  }),

  // Custom pattern validation
  customPattern: (fieldName: string, pattern: RegExp, message?: string) => ({
    pattern: {
      value: pattern,
      message: message || `${fieldName} format is invalid`
    }
  })
};

// Step-level validation functions for form flow control
export const StepValidations = {
  // Generic single field validation
  singleField: <T extends BaseFormData>(fieldName: keyof T) => (data: T): boolean => {
    return !!(data[fieldName]);
  },

  // Multiple required fields validation
  multipleFields: <T extends BaseFormData>(fieldNames: (keyof T)[]) => (data: T): boolean => {
    return fieldNames.every(fieldName => !!(data[fieldName]));
  },

  // ABN lookup step validation
  abnLookup: <T extends BaseFormData>(data: T): boolean => {
    return !!(data.selectedABN && data.businessName && data.postcode);
  },

  // Business details validation
  businessDetails: <T extends BaseFormData>(data: T): boolean => {
    const industryValid = data.industry === "Other" 
      ? !!(data.customIndustry && typeof data.customIndustry === 'string' && data.customIndustry.trim().length > 0)
      : !!(data.industry && data.industry !== 'Select');
    
    return !!(
      data.countryOfRegistration &&
      data.state &&
      data.businessType &&
      industryValid &&
      data.numberOfEmployees &&
      data.annualTurnover
    );
  },

  // Contact details validation
  contactDetails: <T extends BaseFormData>(data: T): boolean => {
    return !!(
      data.firstName &&
      data.lastName &&
      data.workEmail &&
      data.phone &&
      data.termsAccepted
    );
  },

  // Conditional validation based on other fields
  conditional: <T extends BaseFormData>(
    condition: (data: T) => boolean,
    validation: (data: T) => boolean
  ) => (data: T): boolean => {
    if (condition(data)) {
      return validation(data);
    }
    return true; // Skip validation if condition not met
  },

  // Custom validation function
  custom: <T extends BaseFormData>(validationFn: (data: T) => boolean) => validationFn
};

// Error message formatter for consistent error display
export const formatValidationError = (error: string): string => {
  // Capitalize first letter
  const formatted = error.charAt(0).toUpperCase() + error.slice(1);
  
  // Add period if not present
  return formatted.endsWith('.') ? formatted : `${formatted}.`;
};

// Validation helper for React Hook Form integration
export const createRHFValidation = (rules: Record<string, unknown>) => {
  return rules;
};

// Type-safe validation rule builder
export class ValidationBuilder {
  private rules: Record<string, unknown> = {};

  required(fieldName: string) {
    Object.assign(this.rules, ValidationRules.required(fieldName));
    return this;
  }

  email(fieldName?: string) {
    Object.assign(this.rules, ValidationRules.email(fieldName));
    return this;
  }

  phone(fieldName?: string) {
    Object.assign(this.rules, ValidationRules.phone(fieldName));
    return this;
  }

  minLength(fieldName: string, min: number) {
    Object.assign(this.rules, ValidationRules.minLength(fieldName, min));
    return this;
  }

  maxLength(fieldName: string, max: number) {
    Object.assign(this.rules, ValidationRules.maxLength(fieldName, max));
    return this;
  }

  custom(validation: Record<string, unknown>) {
    Object.assign(this.rules, validation);
    return this;
  }

  build() {
    return this.rules;
  }
}

// Factory function for creating validation builders
export const createValidation = () => new ValidationBuilder();
